"use client";

import AuthorAvatar from "@/components/gioi-thieu/AuthorAvatar";
import SmartSidebarTOC from "@/components/gioi-thieu/SmartSidebarTOC";
import FloatingTOCPopup from "@/components/gioi-thieu/FloatingTOCPopup";
import AwardImage from "@/components/gioi-thieu/AwardImage";
import TestimonialsGrid from "@/components/gioi-thieu/TestimonialsGrid";
import Image from "next/image";
import Link from "next/link";
import {
  BookOpen,
  Award,
  Calendar,
  Eye,
  ShieldCheck,
  ExternalLink,
  AlertTriangle,
  Quote,
  CheckCircle2,
  Phone,
  Mail,
  Facebook,
  MapPin,
  Sparkles,
  Trophy,
} from "lucide-react";

/**
 * Trang Giới thiệu JB.com.vn
 * Route đề xuất: /gioi-thieu hoặc /about
 *
 * Schema markup được đặt trong app/gioi-thieu/layout.tsx
 * (Next.js không cho phép <script type="application/ld+json"> trong client component
 *  nên phải tách ra)
 */
export default function GioiThieuPage() {
  return (
  <div className="min-h-screen bg-[#f1f5f9] animate-fade-slide">
    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
      <div className="flex gap-8">
        
        {/* TOC sidebar — chỉ hiện trên xl+ */}
        <SmartSidebarTOC />

        {/* Content giữ nguyên max-w-5xl, không bị thu hẹp */}
        <main className="flex-1 min-w-0 max-w-5xl space-y-6">
        {/* ============ HERO CARD ============ */}
        <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-800 to-teal-900 text-white shadow-card-lg">
          {/* Decorative glow */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-teal-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

          <div className="relative px-6 sm:px-10 py-10 sm:py-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-xs font-medium text-teal-100 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-orange-400" />
              <span className="uppercase tracking-wider">Giới thiệu · Hoạt động từ 2009</span>
            </div>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5 max-w-3xl">
              Nền tảng học và thi thử ngân hàng{" "}
              <span className="text-orange-400 italic">được vận hành bởi chính tác giả sách</span>
              {" "}
            </h2>

            <p className="text-base sm:text-lg text-teal-100/90 max-w-2xl leading-relaxed mb-8">
              JB.com.vn - web.Jb.com.vn là dự án thi thử trực tuyến của tác giả{" "}
              <strong className="text-white">Nguyễn Lê Giang</strong> - chuyên gia Tài chính - Ngân hàng
              với hơn 15 năm kinh nghiệm, 22 đầu sách chuyên khảo, trong đó cuốn{" "}
              <em>"Nghề Ngân Hàng - Những chuyện chưa kể"</em> hiện được lưu trữ tại Thư viện Quốc gia Việt Nam.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6 border-t border-white/10">
              {[
                { num: "15+", label: "Năm kinh nghiệm" },
                { num: "22", label: "Đầu sách bản quyền" },
                { num: "2009", label: "Năm khởi đầu" },
                { num: "3K+", label: "Lượt xem/ngày" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="font-display text-2xl sm:text-3xl font-bold text-orange-400 leading-none">
                    {stat.num}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-teal-200/70 mt-2 font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ TÁC GIẢ ============ */}
        <section className="bg-card rounded-2xl shadow-card p-6 sm:p-8">
          <SectionHeader
            eyebrow="Tác giả · Founder"
            title="Người đứng sau JB.com.vn"
          />

          <p className="text-muted-foreground leading-relaxed mb-6">
            Khác với các website bán đề thi ẩn danh, JB.com.vn công khai hoàn toàn về tác giả
            và đội ngũ. Bạn biết rõ ai là người viết tài liệu, ai chịu trách nhiệm về nội dung,
            và có thể kiểm chứng độc lập mọi thông tin được nêu dưới đây.
          </p>

          {/* Author Card */}
<div className="grid sm:grid-cols-[200px_1fr] gap-6 sm:gap-8 p-6 sm:p-8 rounded-xl bg-gradient-to-br from-teal-50 to-orange-100/50 border-l-4 border-teal-700">
  <AuthorAvatar />

            <div>
              <h3 className="font-display text-2xl font-bold text-teal-900 mb-1">
                Nguyễn Lê Giang
              </h3>
              <p className="text-sm font-semibold text-orange-600 uppercase tracking-wide mb-4">
                Tác giả · Founder JB.com.vn · GiangBLOG
              </p>

              <ul className="space-y-2.5 text-sm text-foreground/80">
                <CredItem icon={<BookOpen className="w-4 h-4 text-teal-700" />}>
                  Tác giả <strong>22 đầu sách</strong> Tài chính - Ngân hàng - Đầu tư
                </CredItem>
                <CredItem icon={<Award className="w-4 h-4 text-teal-700" />}>
                  Có sách lưu trữ tại <strong>Thư viện Quốc gia Việt Nam</strong>
                </CredItem>
                <CredItem icon={<Calendar className="w-4 h-4 text-teal-700" />}>
                  <strong>15+ năm</strong> hoạt động trong ngành tài chính - ngân hàng
                </CredItem>
                <CredItem icon={<Eye className="w-4 h-4 text-teal-700" />}>
                  Sáng lập GiangBLOG từ <strong>25/06/2009</strong> — 3000+ lượt xem/ngày
                </CredItem>
                <CredItem icon={<Sparkles className="w-4 h-4 text-teal-700" />}>
                  Phát triển hệ sinh thái: jb.com.vn, thinganhang.com, chat.jb.com.vn
                </CredItem>
              </ul>
            </div>
          </div>
        </section>

        {/* ============ GIẢI THƯỞNG & GHI NHẬN ============ */}
<section className="bg-card rounded-2xl shadow-card p-6 sm:p-8">
  <SectionHeader
    eyebrow="Awards · Giải thưởng"
    title="Ghi nhận từ tổ chức giáo dục quốc tế"
  />

  <p className="text-muted-foreground leading-relaxed mb-6">
    Bên cạnh hệ thống học liệu thi tuyển ngân hàng tại JB.com.vn, tác giả Nguyễn Lê Giang
    còn phát triển các sản phẩm giáo dục ứng dụng AI. Những ghi nhận dưới đây giúp xác minh
    chuyên môn của tác giả từ các tổ chức bên thứ ba có uy tín:
  </p>

  {/* Award Card */}
  <div className="grid lg:grid-cols-[1fr_280px] gap-6 items-stretch">
    {/* Left: Description */}
    <div className="p-6 sm:p-7 rounded-xl bg-gradient-to-br from-teal-50 to-orange-100/40 border-l-4 border-orange-500">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-5 h-5 text-orange-600" />
        <span className="text-xs font-bold uppercase tracking-wider text-orange-600">
          STEAMese Festival 2025
        </span>
      </div>

      <h3 className="font-display text-xl sm:text-2xl font-bold text-teal-900 mb-3 leading-snug">
  
    <a href="https://web.facebook.com/photo?fbid=1089528480017840&set=pcb.1089529060017782"
    target="_blank"
    rel="noopener noreferrer"
    className="hover:text-orange-600 hover:underline transition-colors inline-flex items-center gap-2 group"
  >
    Top 20 - AI For SuperTeachers Award
    <ExternalLink className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
  </a>
</h3>

      <p className="text-sm text-foreground/80 leading-relaxed mb-4">
        Năm 2025, sáng kiến{" "}
        <strong>"Trợ lý ảo AI - Goupee"</strong> do tác giả phát triển được chọn vào{" "}
        <strong>Top 20 cuộc thi AI For SuperTeachers Award</strong> tại STEAMese Festival 2025
        - một trong những sự kiện giáo dục STEM lớn nhất Việt Nam.
      </p>

      {/* Organizing entities */}
      <div className="pt-4 border-t border-teal-200/60">
        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">
          Đơn vị tổ chức & bảo trợ
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            "STEAM for Vietnam",
            "UNICEF Việt Nam",
            "Đại sứ quán Hoa Kỳ tại Hà Nội",
          ].map((org) => (
            <span
              key={org}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-border text-xs font-medium text-teal-800"
            >
              <CheckCircle2 className="w-3 h-3 text-teal-600" />
              {org}
            </span>
          ))}
        </div>
      </div>

      {/* Signers - tăng độ tin cậy */}
      <div className="pt-4 mt-4 border-t border-teal-200/60 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="font-semibold text-teal-900">TS. Trần Việt Hùng</div>
          <div className="text-muted-foreground">
            Đồng sáng lập & Giám đốc<br />STEAM for Vietnam
          </div>
        </div>
        <div>
          <div className="font-semibold text-teal-900">Tara O'Connell</div>
          <div className="text-muted-foreground">
            Trưởng Chương trình Giáo dục<br />UNICEF Việt Nam
          </div>
        </div>
      </div>
    </div>

    {/* Right: Certificate image */}
    <AwardImage />
  </div>

  {/* Disclaimer minh bạch - quan trọng cho E-E-A-T */}
  <p className="text-xs text-muted-foreground mt-5 italic">
    Goupee.com hiện đang trong giai đoạn miễn phí, phục vụ cộng đồng giáo viên Việt Nam.
    Dự án độc lập với JB.com.vn nhưng cùng được phát triển bởi tác giả Nguyễn Lê Giang.
  </p>
</section>

        {/* ============ KIỂM CHỨNG ============ */}
        <section className="bg-card rounded-2xl shadow-card p-6 sm:p-8">
          <SectionHeader
            eyebrow="Verification · Kiểm chứng độc lập"
            title="Những thông tin bạn có thể tự xác minh"
          />

          <p className="text-muted-foreground leading-relaxed mb-6">
            Trong ngành đào tạo có rất nhiều tuyên bố không thể kiểm chứng. JB.com.vn chỉ liệt kê
            những gì bạn có thể xác minh độc lập từ nguồn bên thứ ba:
          </p>

          {/* Trust links */}
          <div className="rounded-xl bg-teal-900 text-white p-6 sm:p-7 mb-6">
            <h3 className="font-display text-lg font-semibold text-orange-400 mb-4 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              Đường dẫn kiểm chứng chính thức
            </h3>
            <ul className="space-y-3 text-sm">
              <VerifyLink
                label="Thư viện Quốc gia Việt Nam"
                desc="Tra cứu sách 'Nghề Ngân Hàng - Những chuyện chưa kể'"
                href="https://opac.nlv.gov.vn/chi-tiet-tai-lieu/nghe-ngan-hang---nhung-chuyen-chua-ke-20267866010302174820"
              />
              <VerifyLink
                label="Lịch sử hoạt động blog"
                desc="GiangBLOG đăng ký từ 25/06/2009 — tra cứu tại Archive.ph"
                href="https://archive.ph/vj3Ku/again?url=http://giangblog.com/"
              />
              <VerifyLink
                label="Danh mục 22 đầu sách bản quyền"
                desc="Liệt kê công khai trên giangblog.com"
                href="https://www.giangblog.com/p/huong-dan-mua-sach.html"
              />
              <VerifyLink
                label="Fanpage chính thức Đề thi Ngân hàng"
                desc="Hoạt động liên tục 12+ năm"
                href="https://www.facebook.com/dethivaonganhang"
              />
              <VerifyLink
                label="Fanpage chính thức GiangBLOG"
                desc="Hoạt động liên tục 15+ năm"
                href="https://www.facebook.com/giangblog"
              />
            </ul>
          </div>

          {/* Ecosystem */}
          <h3 className="font-display text-xl font-semibold text-teal-900 mb-4">
            Hệ sinh thái sản phẩm dưới cùng một thương hiệu
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { num: "01", domain: "giangblog.com", desc: "Blog gốc, sáng lập năm 2009" },
              { num: "02", domain: "jb.com.vn", desc: "Nền tảng thi thử trực tuyến" },
              { num: "03", domain: "thinganhang.com", desc: "Thư viện tài liệu" },
              { num: "04", domain: "chat.jb.com.vn", desc: "Trợ lý AI luyện thi" },
            ].map((item) => (
              <div
                key={item.domain}
                className="p-4 rounded-xl border border-border bg-background hover:border-teal-500 hover:shadow-card-md transition-all"
              >
                <div className="font-display text-2xl font-bold text-orange-500 leading-none mb-2">
                  {item.num}
                </div>
                <div className="font-semibold text-sm text-teal-900 mb-1">{item.domain}</div>
                <div className="text-xs text-muted-foreground">{item.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ============ TIMELINE ============ */}
        <section className="bg-card rounded-2xl shadow-card p-6 sm:p-8">
          <SectionHeader
            eyebrow="Lịch sử · Timeline"
            title="15 năm xây dựng niềm tin từng cuốn sách một"
          />

          <div className="relative pl-8 sm:pl-10 mt-6">
            {/* Vertical line */}
            <div className="absolute left-2.5 sm:left-3 top-2 bottom-2 w-px bg-border" />

            {[
              {
                year: "06 / 2009",
                title: "Khởi đầu GiangBLOG",
                desc: "Một trong những blog tiếng Việt đầu tiên viết chuyên sâu về tuyển dụng ngân hàng. Chia sẻ miễn phí đề thi kèm đáp án.",
              },
              {
                year: "2012 – 2018",
                title: "Phát hành sách bản quyền",
                desc: "Lần lượt xuất bản cẩm nang thi Giao dịch viên, Tín dụng - QHKH, Kế toán ngân hàng, Thanh toán quốc tế, Vietcombank, BIDV, Vietinbank, Sacombank, HDBank, MB, Agribank...",
              },
              {
                year: "04 / 2018",
                title: "Sách thứ 18 vào Thư viện Quốc gia",
                desc: "'Nghề Ngân Hàng - Những chuyện chưa kể' phát hành bản in giấy, hiện được lưu trữ và phục vụ độc giả tại Thư viện Quốc gia Việt Nam.",
              },
              {
                year: "2019",
                title: "Chuyển sang nền tảng JB.com.vn",
                desc: "Sau nhiều năm phát hành sách PDF qua email và bị sao chép trái phép, tác giả quyết định chuyển toàn bộ tài liệu sang nền tảng thi thử trực tuyến để bảo vệ bản quyền và cập nhật liên tục.",
              },
              {
                year: "2024 – 2025",
                title: "Tích hợp AI vào học liệu",
                desc: "Ra mắt chat.jb.com.vn - phiên bản AI của tác giả, hỗ trợ học viên luyện thi 24/7 với chính nội dung từ các đầu sách gốc.",
              },
              {
                year: "2026 – now",
                title: "Nền tảng học thi thông minh JB AI",
                desc: "Ra mắt web.jb.com.vn - Nền tảng học thi thông minh kết hợp AI để cá nhân hóa lộ trình của riêng bạn.",
              },
            ].map((item, idx) => (
              <div key={idx} className="relative pb-7 last:pb-0">
                <div className="absolute -left-[31px] sm:-left-[37px] top-1.5 w-3 h-3 rounded-full bg-orange-500 ring-4 ring-card shadow-[0_0_0_1px_hsl(var(--border))]" />
                <div className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-1">
                  {item.year}
                </div>
                <div className="font-display text-lg font-semibold text-teal-900 mb-1">
                  {item.title}
                </div>
                <div className="text-sm text-muted-foreground leading-relaxed">{item.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ============ DANH MỤC SÁCH ============ */}
        <section className="bg-card rounded-2xl shadow-card p-6 sm:p-8">
          <SectionHeader
            eyebrow="Bibliography · Tác phẩm"
            title="22 đầu sách bản quyền"
          />

          <p className="text-muted-foreground leading-relaxed mb-5">
            Các gói thi trên JB.com.vn được xây dựng và kiểm chứng từ Tác giả. Sách bản quyền tại giangblog.com -
            không phải nội dung sưu tầm từ internet:
          </p>

          <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {BOOK_LIST.map((book, idx) => (
              <li
                key={idx}
                className="flex items-baseline gap-3 py-2 border-b border-dashed border-border/70 last:border-0"
              >
                <span className="font-mono text-xs text-orange-500 font-bold w-6 shrink-0">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span className={book.featured ? "font-semibold text-teal-900" : "text-foreground/80"}>
                  {book.title}
                  {book.featured && (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold uppercase tracking-wide">
                      <Award className="w-3 h-3" />
                      Thư viện Quốc gia
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </section>

        {/* ============ CẢNH BÁO ============ */}
        <section className="bg-card rounded-2xl shadow-card p-6 sm:p-8">
          <SectionHeader
            eyebrow="Bản quyền · Copyright Notice"
            title="Lưu ý về tình trạng sao chép trái phép"
          />

          <p className="text-muted-foreground leading-relaxed mb-6">
            Trong vài năm gần đây, một số fanpage và website đã mua tài liệu từ JB.com.vn rồi
            chụp ảnh, bán lại với giá rẻ hơn dưới danh nghĩa "tài liệu sưu tầm". Một số nơi
            còn đăng bài bôi nhọ uy tín nhằm hướng người dùng sang sản phẩm của họ.
          </p>

          <div className="rounded-xl border-2 border-destructive/30 bg-destructive/5 p-5 sm:p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="shrink-0 w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold text-destructive mb-1">
                  Cách phân biệt nguồn gốc tài liệu
                </h3>
                <p className="text-sm text-muted-foreground">
                  Tài liệu chính chủ từ JB.com.vn / GiangBLOG luôn có 4 đặc điểm sau:
                </p>
              </div>
            </div>

            <ul className="space-y-2.5 pl-13">
              {[
                "Tên tác giả 'Nguyễn Lê Giang' trên trang bìa và footer mỗi tài liệu nếu là bản pdf",
                "Được truy cập qua tài khoản đăng ký tại jb.com.vn",
                "Đồng liên kết đến giangblog.com hoặc thinganhang.com",
                "Cập nhật định kỳ theo từng kỳ tuyển dụng ngân hàng",
                "Công khai số điện thoại bạn có thể liên hệ để xác minh",
                "Tài khoản ngân hàng: Vietcombank - Nguyễn Lê Giang - Số TK: 9989390127 hoặc 0611001892082",
              ].map((line, idx) => (
                <li key={idx} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />
                  <span className="text-foreground/80">{line}</span>
                </li>
              ))}
            </ul>

            <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-destructive/20">
              Mọi trường hợp phát hiện sao chép trái phép, xin liên hệ trực tiếp với chúng tôi.
              Tài liệu sao chép không được tác giả bảo trợ, không cập nhật, và có thể chứa sai sót
              so với bản gốc.
            </p>
          </div>
        </section>

       {/* ============ TESTIMONIALS ============ */}
        <TestimonialsGrid />

        {/* ============ SỨ MỆNH (DARK) ============ */}
        <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-900 to-teal-800 text-white p-6 sm:p-10 shadow-card-lg">
          <div className="absolute top-0 right-0 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />

          <div className="relative">
            <div className="inline-block px-3 py-1 rounded-full bg-orange-500/20 border border-orange-400/30 text-xs font-bold uppercase tracking-wider text-orange-300 mb-4">
              Mission · Sứ mệnh
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-6">
              Tại sao JB.com.vn tồn tại
            </h2>

            <div className="space-y-4 text-teal-100/90 leading-relaxed max-w-3xl">
              <p>
                Sau 9 năm phát hành sách PDF qua email, tác giả nhận ra một thực tế: mỗi cuốn sách
                bán ra sẽ bị chia sẻ trái phép trong vòng vài tuần. Người mua thật chịu thiệt,
                người sao chép hưởng lợi, và chất lượng nội dung đến tay thí sinh ngày càng méo mó
                qua mỗi lần copy.
              </p>
              <p>
                JB.com.vn ra đời để giải quyết bài toán đó: sách, câu hỏi thi nằm trên nền tảng được kiểm soát,
                cập nhật sau mỗi kỳ thi mới, và chính tác giả giải đáp, hướng dẫn cho từng học viên.
                Đây không chỉ đơn thuần là các câu hỏi lẻ tẻ - đây là sản phẩm trí tuệ được biên soạn tỉ mỉ từ tác giả
              </p>
            </div>

            {/* Pull quote */}
            <div className="mt-8 p-6 rounded-xl bg-white/5 border-l-4 border-orange-400 backdrop-blur-sm">
              <Quote className="w-8 h-8 text-orange-400/60 mb-3" />
              <p className="font-display italic text-lg leading-relaxed mb-3">
                "Tôi viết cuốn sách đầu tiên năm 2012. Mười hai năm và 22 đầu sách sau, tôi tin rằng
                tài liệu kiến thức luyện thi có rất nhiều, bạn dễ dàng tạo ra nó bởi AI chỉ trong vài giây không thể kiểm chứng,
                cái bạn cần là một Mentor nhiều năm kinh nghiệm đã thực sự làm trong ngành, thực sự tâm huyết và có trách nhiệm,
                Cái bạn cần không chỉ là một đống tài liệu, câu hỏi - mà cần biết nên làm gì là tốt nhất, lộ trình nào hiệu quả nhất với bản thân."
              </p>
              <cite className="not-italic text-xs uppercase tracking-wider text-orange-300 font-semibold">
                — Nguyễn Lê Giang, Founder JB.com.vn
              </cite>
            </div>
          </div>
        </section>

        {/* ============ CONTACT ============ */}
        <section className="bg-card rounded-2xl shadow-card p-6 sm:p-8">
          <SectionHeader
            eyebrow="Contact · Liên hệ"
            title="Liên hệ trực tiếp với tác giả"
          />

          <p className="text-muted-foreground leading-relaxed mb-6">
            Mọi câu hỏi về sách, yêu cầu hỗ trợ ôn thi, hoặc tố cáo sao chép trái phép,
            vui lòng liên hệ:
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <ContactCard
              icon={<Phone className="w-4 h-4" />}
              label="Hotline / Zalo"
              value="0989 390 127"
              href="tel:+84989390127"
            />
            <ContactCard
              icon={<Mail className="w-4 h-4" />}
              label="Email"
              value="lienhejb@gmail.com"
              href="mailto:lienhejb@gmail.com"
            />
            <ContactCard
              icon={<Facebook className="w-4 h-4" />}
              label="Facebook"
              value="fb.com/dethivaonganhang"
              href="https://www.facebook.com/dethivaonganhang"
              external
            />
            <ContactCard
              icon={<MapPin className="w-4 h-4" />}
              label="Tư vấn trực tiếp"
              value="Hà Nội (đặt lịch trước)"
            />
          </div>
        </section>

        {/* ============ FOOTER ============ */}
        <footer className="text-center text-xs text-muted-foreground py-6">
          © 2009 – 2026 JB.com.vn · GiangBLOG · Nguyễn Lê Giang. All rights reserved.
          <br />
          Tài liệu trên website này được bảo hộ bản quyền theo Luật Sở hữu trí tuệ Việt Nam.
        </footer>
        </main>
  </div>
      </div>
    {/* Floating popup cho mobile/tablet */}
    <FloatingTOCPopup />
  </div>
  );
}

/* ============ SUB-COMPONENTS ============ */

function SectionHeader({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="mb-5">
      <div className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-2">
        {eyebrow}
      </div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-teal-900">{title}</h2>
    </div>
  );
}

function CredItem({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="shrink-0 mt-0.5">{icon}</span>
      <span>{children}</span>
    </li>
  );
}

function VerifyLink({
  label,
  desc,
  href,
}: {
  label: string;
  desc: string;
  href: string;
}) {
  return (
    <li className="group">
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-start gap-3 py-2.5 px-3 -mx-3 rounded-lg hover:bg-white/5 transition-colors"
      >
        <ExternalLink className="w-4 h-4 text-orange-400 shrink-0 mt-0.5 group-hover:rotate-12 transition-transform" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-orange-300">{label}</div>
          <div className="text-xs text-teal-100/70 mt-0.5">{desc}</div>
        </div>
      </Link>
    </li>
  );
}

function ContactCard({
  icon,
  label,
  value,
  href,
  external,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
  external?: boolean;
}) {
  const content = (
    <div className="p-4 rounded-xl bg-gradient-to-br from-background to-teal-50/30 border-t-2 border-teal-700 hover:shadow-card-md transition-all h-full">
      <div className="flex items-center gap-2 mb-2 text-teal-700">
        {icon}
        <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
          {label}
        </span>
      </div>
      <div className="font-semibold text-sm text-foreground">{value}</div>
    </div>
  );

  if (href) {
    return (
      <Link
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
      >
        {content}
      </Link>
    );
  }
  return content;
}

/* ============ DATA ============ */

const BOOK_LIST = [
  { title: "Cẩm nang Giao dịch viên ngân hàng" },
  { title: "Cẩm nang Tín dụng - Quan hệ khách hàng" },
  { title: "Cẩm nang Thanh toán quốc tế" },
  { title: "Tuyển tập đề thi Kế toán ngân hàng (kèm đáp án)" },
  { title: "Tuyển tập đề thi vào các ngân hàng" },
  { title: "Tuyển tập câu hỏi phỏng vấn ngân hàng (song ngữ)" },
  { title: "300 câu phỏng vấn Tín dụng - QHKH và trả lời mẫu" },
  { title: "Tuyển tập câu hỏi Anh Văn thi ngân hàng" },
  { title: "Cẩm nang thi tuyển Vietcombank" },
  { title: "Cẩm nang thi tuyển Vietinbank" },
  { title: "Cẩm nang thi tuyển Agribank" },
  { title: "Cẩm nang thi tuyển BIDV (phỏng vấn)" },
  { title: "Cẩm nang thi tuyển Sacombank" },
  { title: "Cẩm nang thi tuyển HDBank" },
  { title: "Cẩm nang đề thi - phỏng vấn MB Bank" },
  { title: "Cẩm nang thi NH Chính sách Xã hội (VBSP)" },
  { title: "Cẩm nang thi tuyển Viettel" },
  { title: "Cẩm nang thi GSAT Samsung" },
  { title: "Nghề Ngân Hàng - Những chuyện chưa kể", featured: true },
  { title: "Bộ câu hỏi ôn ngân quỹ Vietcombank" },
  { title: "Tuyển tập IQ - GMAT - Logic thi ngân hàng" },
  { title: "Bộ câu hỏi trắc nghiệm tin học Agribank" },
];